import { C } from "@/brand/tokens";
import { PHOTOS } from "@/constants/photos";
import { supportArchitectures } from "@/brand/content";
import { ChamberChrome } from "@/components/common/ChamberChrome";

/**
 * HowSupportWorksSection — Dry Stone chamber (III. ARCHITECTURE OF SUPPORT).
 *
 * Authoring moves:
 *   - Chamber chrome rail at top
 *   - Facilitation image fragment BLEEDS to the viewport right edge
 *     (mirrors hero portrait bleed) with the same seam architecture:
 *     6px Slate Olive doorframe + 1px brass hairline + inset shadow.
 *   - Text rail compressed to col 1-6, image takes the bleed
 *   - Brass-labeled architectural caption sits AT the seam, locked to
 *     the image's left edge (echoing the hero quote card)
 */
export function HowSupportWorksSection() {
  const IMG_W = "38%";

  return (
    <section
      data-testid="how-support-works"
      style={{
        background: C.surfaceLight,
        color: C.textOnLight,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChamberChrome
        numeral="III."
        title="Architecture of support"
        category="How support works"
        meta="Three architectures"
        tone="light"
      />

      <div className="relative">
        {/* Seam architecture: Slate Olive doorframe + brass hairline */}
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-0 bottom-0"
          style={{
            right: `calc(${IMG_W} + 1px)`,
            width: 6,
            background: C.supportOrganic,
          }}
        />
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-0 bottom-0"
          style={{ right: IMG_W, width: 1, background: C.accentMetal }}
        />

        {/* Facilitation image — bleeds to viewport right */}
        <figure
          className="hidden lg:block absolute top-0 right-0 bottom-0"
          style={{
            width: IMG_W,
            boxShadow: "inset 40px 0 60px -32px rgba(22,24,27,0.55)",
          }}
        >
          <div
            className="absolute inset-0"
            style={{ background: C.supportOrganic }}
          />
          <img
            src={PHOTOS.facilitation}
            alt="Faith 1st in practice — confidential dialogue"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              objectPosition: "30% center",
              filter: "saturate(0.78) contrast(1.05) brightness(0.92)",
            }}
            loading="lazy"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(180deg, rgba(22,24,27,0.18) 0%, rgba(22,24,27,0) 30%, rgba(22,24,27,0.5) 100%)`,
            }}
          />

          {/* Architectural caption — locked to image's LEFT edge */}
          <figcaption
            className="absolute"
            style={{
              right: "100%",
              top: "62%",
              width: 260,
              background: C.surfaceLight,
              borderTop: `1px solid ${C.accentMetal}`,
              borderLeft: `1px solid ${C.borderSoft}`,
              borderBottom: `1px solid ${C.borderSoft}`,
              padding: "18px 22px",
              boxShadow: "-24px 24px 48px -24px rgba(22,24,27,0.25)",
            }}
          >
            <p
              className="font-inter text-[9.5px] uppercase font-bold mb-3"
              style={{ color: C.accentMetal, letterSpacing: "0.30em" }}
            >
              Documentary fragment
            </p>
            <p
              className="font-cormorant italic leading-[1.4]"
              style={{ color: C.textOnLight, fontSize: "0.96rem" }}
            >
              Where the work actually happens — held in conversation, not in
              presentations.
            </p>
          </figcaption>
        </figure>

        {/* Text rail */}
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14 py-24 lg:py-32 relative">
          <div className="lg:max-w-[640px]">
            <div className="flex items-center gap-3 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-10"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold tabular-nums"
                style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
              >
                § 03
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(1.8rem, 2.8vw, 2.6rem)",
                fontWeight: 400,
                lineHeight: 1.08,
                letterSpacing: "-0.005em",
              }}
            >
              Three architectures of support,
              <br />
              scoped to the moment
              <br />
              your organization is in.
            </h2>

            <ol
              className="mt-12 space-y-0"
              style={{ borderTop: `1px solid ${C.borderSoft}` }}
            >
              {supportArchitectures.map((b, i) => (
                <li
                  key={b.label}
                  data-testid={`support-architecture-${i}`}
                  className="py-7 grid grid-cols-12 gap-x-4"
                  style={{ borderBottom: `1px solid ${C.paperOver}` }}
                >
                  <span
                    className="col-span-2 font-cormorant tabular-nums"
                    style={{
                      color: C.accentMetal,
                      fontSize: "1.8rem",
                      fontWeight: 400,
                      lineHeight: 1,
                    }}
                  >
                    {b.n}
                  </span>
                  <div className="col-span-10">
                    <h3
                      className="font-inter text-[11px] uppercase font-semibold mb-3"
                      style={{
                        color: C.accentFunctional,
                        letterSpacing: "0.28em",
                      }}
                    >
                      {b.label}
                    </h3>
                    <p
                      className="font-inter leading-[1.65] max-w-[44ch]"
                      style={{ color: C.inkOnLight, fontSize: "0.98rem" }}
                    >
                      {b.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Mobile-only image */}
          <div className="lg:hidden mt-14">
            <figure className="relative">
              <div
                className="relative overflow-hidden"
                style={{
                  aspectRatio: "4 / 5",
                  background: C.supportOrganic,
                  border: `1px solid ${C.borderSoft}`,
                }}
              >
                <img
                  src={PHOTOS.facilitation}
                  alt="Faith 1st in practice — confidential dialogue"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    objectPosition: "30% center",
                    filter: "saturate(0.78) brightness(0.95)",
                  }}
                  loading="lazy"
                />
              </div>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
